import { CricketASPs, CricketASPsWithThrow, CricketGameState, CricketPlayerState, CricketResults, CricketTeamState } from "../types/cricket";
import { PLAYER } from '../types/players';
import { GAME_STATE } from '../types/game';
import { CRICKET_NUMBERS, CRICKET_BULL_ASP, CRICKET_MARK_ASP, BOTTOM_BUTTONS } from "../types/dart";
import { cloneDeep } from "lodash";
import { BOT_DIFFICULTY, MATCH_TYPE } from "../types/match";
import { botHitChance, getNextThrower } from "./matchLogic";
import { playAudio } from "./sounds";
import { PlayerTeamIdx, TEAM } from "../types/teams";
import { getOpponentsTeam, getPlayersTeam } from "./teamLogic";

const initPlayerState: CricketPlayerState = {
  name: '',
  totalMarks: 0,
  totalThrows: 0,
  currentHits: {
    marks: 0,
    bulls: 0
  },
  asps: {
    marks: {
      [CRICKET_MARK_ASP.FIVE]: 0,
      [CRICKET_MARK_ASP.SIX]: 0,
      [CRICKET_MARK_ASP.SEVEN]: 0,
      [CRICKET_MARK_ASP.EIGHT]: 0,
      [CRICKET_MARK_ASP.NINE]: 0,
    },
    bulls: {
      [CRICKET_BULL_ASP.THREE]: 0,
      [CRICKET_BULL_ASP.FOUR]: 0,
      [CRICKET_BULL_ASP.FIVE]: 0,
      [CRICKET_BULL_ASP.SIX]: 0,
    }
  }
};

export const initPlayerResultASPs: CricketASPsWithThrow = {
  total: {
    throws: 0,
    marks: 0,
  },
  marks: {
    [CRICKET_MARK_ASP.FIVE]: 0,
    [CRICKET_MARK_ASP.SIX]: 0,
    [CRICKET_MARK_ASP.SEVEN]: 0,
    [CRICKET_MARK_ASP.EIGHT]: 0,
    [CRICKET_MARK_ASP.NINE]: 0,
  },
  bulls: {
    [CRICKET_BULL_ASP.THREE]: 0,
    [CRICKET_BULL_ASP.FOUR]: 0,
    [CRICKET_BULL_ASP.FIVE]: 0,
    [CRICKET_BULL_ASP.SIX]: 0,
  }
}

const initCricketGameState: CricketGameState = {
  gameState: GAME_STATE.ACTIVE,
  throwNumber: 1,
  currentPlayer: PLAYER.FIRST,
  [TEAM.ONE]: {
    score: 0,
    numbers: {
      [CRICKET_NUMBERS.TWENTY]: 0,
      [CRICKET_NUMBERS.NINETEEN]: 0,
      [CRICKET_NUMBERS.EIGHTEEN]: 0,
      [CRICKET_NUMBERS.SEVENTEEN]: 0,
      [CRICKET_NUMBERS.SIXTEEN]: 0,
      [CRICKET_NUMBERS.FIFTEEN]: 0,
      [CRICKET_NUMBERS.BULL]: 0
    },
    players: [{
      ...cloneDeep(initPlayerState),
      name: PLAYER.FIRST,
    }],
  },
  [TEAM.TWO]: {
    score: 0,
    numbers: {
      [CRICKET_NUMBERS.TWENTY]: 0,
      [CRICKET_NUMBERS.NINETEEN]: 0,
      [CRICKET_NUMBERS.EIGHTEEN]: 0,
      [CRICKET_NUMBERS.SEVENTEEN]: 0,
      [CRICKET_NUMBERS.SIXTEEN]: 0,
      [CRICKET_NUMBERS.FIFTEEN]: 0,
      [CRICKET_NUMBERS.BULL]: 0
    },
    players: [{
      ...cloneDeep(initPlayerState),
      name: PLAYER.SECOND,
    }],
  }
}


export function getInitCricketState(matchType: MATCH_TYPE) {
  const newGameState = cloneDeep(initCricketGameState);
  if (matchType === MATCH_TYPE.DOUBLES) {
    newGameState.team1.players.push({ ...cloneDeep(initPlayerState), name: PLAYER.THIRD });
    newGameState.team2.players.push({ ...cloneDeep(initPlayerState), name: PLAYER.FOURTH });
  }
  return newGameState;
}

export const initResults: CricketResults = {
  [TEAM.ONE]: [],
  [TEAM.TWO]: [],
};

export const numberOrder: CRICKET_NUMBERS[] = [
  CRICKET_NUMBERS.TWENTY,
  CRICKET_NUMBERS.NINETEEN,
  CRICKET_NUMBERS.EIGHTEEN,
  CRICKET_NUMBERS.SEVENTEEN,
  CRICKET_NUMBERS.SIXTEEN,
  CRICKET_NUMBERS.FIFTEEN,
  CRICKET_NUMBERS.BULL,
];


function getOpponent(cricketState: CricketGameState): { opponent: PLAYER, opponentState: CricketTeamState } {
  const { currentPlayer } = cricketState;
  let opponent: PLAYER = getNextThrower(cricketState);
  const opponentTeam: TEAM = getOpponentsTeam(currentPlayer);

  return {
    opponent,
    opponentState: cricketState[opponentTeam]
  };
}

function getASPs(cricketState: CricketGameState): CricketASPs {
  const team = getPlayersTeam(cricketState.currentPlayer);
  let currentPlayerState: CricketPlayerState;
  const { currentPlayer } = cricketState;
  if (team === TEAM.ONE) {
    currentPlayerState = cricketState.team1.players[currentPlayer === PLAYER.FIRST ? 0 : 1];
  } else {
    currentPlayerState = cricketState.team2.players[currentPlayer === PLAYER.SECOND ? 0 : 1];
  }
  const asps = { ...currentPlayerState.asps } as CricketASPs;
  const { marks, bulls } = currentPlayerState.currentHits;
  const hits = marks + bulls;
  if (bulls >= 3) {
    // @ts-ignore
    asps.bulls[bulls] = asps.bulls[bulls] + 1;
  } else if (hits >= 5) {
    // @ts-ignore
    asps.marks[hits] = asps.marks[hits] + 1;
  }

  return asps;
}

function setASPs(cricketState: CricketGameState): CricketGameState {
  const newState = { ...cricketState };
  const asps = getASPs(cricketState);
  const team = getPlayersTeam(cricketState.currentPlayer);
  newState[team].players[PlayerTeamIdx[cricketState.currentPlayer]].asps = asps;
  return newState;
}

function isGameOver(cricketState: CricketGameState): boolean {
  const team = getPlayersTeam(cricketState.currentPlayer);
  const { numbers, score } = cricketState[team];
  const opposingScore = getOpponent(cricketState).opponentState.score;
  if (score < opposingScore) return false;
  for (const cricketNumber of numberOrder) {
    if (numbers[cricketNumber] < 3) {
      return false;
    }
  }
  return true;
}

function changeTurns(cricketState: CricketGameState): CricketGameState {
  const newState = { ...cricketState };
  const { currentPlayer } = cricketState;
  const { opponent } = getOpponent(newState);
  const team = getPlayersTeam(cricketState.currentPlayer);
  setASPs(newState);
  newState[team].players[PlayerTeamIdx[currentPlayer]].currentHits = { marks: 0, bulls: 0 };
  newState.currentPlayer = opponent;
  return newState;
}

function getEndOrMissedState(number: BOTTOM_BUTTONS, cricketState: CricketGameState): CricketGameState {
  const newState = { ...cricketState };
  const { currentPlayer } = cricketState;
  const team = getPlayersTeam(cricketState.currentPlayer);
  playAudio(number);
  if (number === BOTTOM_BUTTONS.MISS) {
    newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows += 1;
    newState.throwNumber = newState.throwNumber === 3 ? 1 : newState.throwNumber + 1;
    if (newState.throwNumber === 1) return changeTurns(newState);
  } else if (number === BOTTOM_BUTTONS.END_TURN) {
    const missedThrows = 4 - newState.throwNumber;
    newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows += missedThrows;
    newState.throwNumber = 1;
    return changeTurns(newState);
  }
  return newState;
};

function setCricketScoreAndMarks(number: CRICKET_NUMBERS, multiplier: number, cricketState: CricketGameState): CricketGameState {
  const { currentPlayer } = cricketState;
  const { opponentState } = getOpponent(cricketState);
  const team = getPlayersTeam(cricketState.currentPlayer);
  const { numbers, score } = cricketState[team];
  const { currentHits: { bulls, marks } } = cricketState[team].players[PlayerTeamIdx[currentPlayer]];
  const currentHitCount = numbers[number];
  const newHitCount = currentHitCount + multiplier;
  let countedMarks = multiplier;

  if (newHitCount <= 3) {
    cricketState[team].numbers[number] = newHitCount;
  } else {
    countedMarks = 0;
    cricketState[team].numbers[number] = 3;
    if (opponentState.numbers[number] < 3) {
      // loops through each dart of the multiplier to see when the game would be over to not count extra hits
      for (let i = 1; i <= multiplier; i++) {
        const newHitCount = currentHitCount + i;
        if (opponentState.numbers[number] < 3) {
          const addedScoreHits = newHitCount - 3;
          const newScore = score + (number * addedScoreHits);
          cricketState[team].score = newScore;
        }
        countedMarks = i;
        if (isGameOver(cricketState)) break;
      }
    } else {
      countedMarks = 3 - currentHitCount;
    }
  }

  if (number === CRICKET_NUMBERS.BULL) {
    cricketState[team].players[PlayerTeamIdx[currentPlayer]].currentHits.bulls = bulls + countedMarks;
  } else {
    cricketState[team].players[PlayerTeamIdx[currentPlayer]].currentHits.marks = marks + countedMarks;
  }

  cricketState[team].players[PlayerTeamIdx[currentPlayer]].totalMarks += countedMarks;
  playAudio(countedMarks);
  return cricketState;
}

export function getBotTurnMarks(botDifficulty: BOT_DIFFICULTY): number {
  let randomMarks = 0;
  switch (botDifficulty) {
    case BOT_DIFFICULTY.EASY:
      randomMarks = Math.random() > .75 ? 2 : 1;
      break;
    case BOT_DIFFICULTY.MEDIUM:
      let randomNumber = Math.random();
      if (randomNumber >= .75) {
        randomMarks = 1;
      } else if (randomNumber <= .25) {
        randomMarks = 3;
      }
      else {
        randomMarks = 2;
      }
      break;
    case BOT_DIFFICULTY.HARD:
      randomMarks = Math.floor(Math.random() * Math.floor(6 - 3)) + 3;
      break;
    // case BOT_DIFFICULTY.PRO:
    //   randomMarks = Math.floor(Math.random() * Math.floor(10 - 5)) + 5;
    //   break;
  }

  return randomMarks;
}

export function getBotTurnBullMarks(botDifficulty: BOT_DIFFICULTY): number {
  let randomMarks = 0;
  switch (botDifficulty) {
    case BOT_DIFFICULTY.EASY:
      randomMarks = Math.random() > .75 ? 1 : 0;
      break;
    case BOT_DIFFICULTY.MEDIUM:
      let randomNumber = Math.random();
      if (randomNumber >= .75) {
        randomMarks = 0;
      } else if (randomNumber <= .25) {
        randomMarks = 2;
      }
      else {
        randomMarks = 1;
      }
      break;
    case BOT_DIFFICULTY.HARD:
      randomMarks = Math.floor(Math.random() * Math.floor(6 - 3));
      break;
    // case BOT_DIFFICULTY.PRO:
    //   randomMarks = Math.floor(Math.random() * Math.floor(10 - 5)) + 5;
    //   break;
  }

  return randomMarks;
}

export function doBotMove(cricketState: CricketGameState, turnMarks: number, botDifficulty: BOT_DIFFICULTY): CricketGameState {
  let nextNumber: CRICKET_NUMBERS | BOTTOM_BUTTONS = findNextNumber(cricketState);
  if (nextNumber === CRICKET_NUMBERS.BULL && !botHitChance(botDifficulty)) {
    return getNextCricketState(BOTTOM_BUTTONS.MISS, BOTTOM_BUTTONS.MISS, cricketState);
  }
  const throwsLeft = 4 - cricketState.throwNumber;
  const marksLeft = turnMarks - cricketState.team2.players[0].currentHits.marks;
  let currentMultiplier = nextNumber !== CRICKET_NUMBERS.BULL ? Math.round(marksLeft / throwsLeft) : 1;
  if (currentMultiplier <= 0) {
    nextNumber = BOTTOM_BUTTONS.MISS;
    currentMultiplier = BOTTOM_BUTTONS.MISS;
  }
  return getNextCricketState(nextNumber, currentMultiplier, cricketState);
}

export function findNextNumber(cricketState: CricketGameState): CRICKET_NUMBERS {
  const { team1, team2 } = cricketState;
  const scoreDifference = team2.score - team1.score;
  for (const cricketNumber of numberOrder) {
    const p1Num = team1.numbers[cricketNumber];
    const p2Num = team2.numbers[cricketNumber];
    if (p2Num === 3 && p1Num === 3) continue;
    if (scoreDifference === 0 && p2Num < 3) {
      return cricketNumber;
    }
    if (scoreDifference > 0 && p2Num === 2) {
      return cricketNumber;
    }
    if (p1Num !== 3 && scoreDifference <= 26) {
      return cricketNumber;
    }
    if (p2Num < 3 && scoreDifference >= 26) {
      return cricketNumber;
    }
    if (p1Num !== 3 && p2Num < 3) {
      return cricketNumber;
    }
  };
  return CRICKET_NUMBERS.BULL;
}
export function getNextCricketState(number: CRICKET_NUMBERS | BOTTOM_BUTTONS, multiplier: number, cricketState: CricketGameState): CricketGameState {

  const newState = cloneDeep(cricketState);
  const { currentPlayer } = newState;
  const team = getPlayersTeam(cricketState.currentPlayer);
  const { totalThrows } = newState[team].players[PlayerTeamIdx[currentPlayer]];

  if (number === BOTTOM_BUTTONS.MISS || number === BOTTOM_BUTTONS.END_TURN) {
    return getEndOrMissedState(number, newState);
  }
  setCricketScoreAndMarks(number, multiplier, newState);
  // Adds one to the throw count
  newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows = totalThrows + 1;
  if (isGameOver(newState)) {
    setASPs(newState);
    newState.gameState = GAME_STATE.PENDING;
    return newState;
  }
  newState.throwNumber = newState.throwNumber === 3 ? 1 : newState.throwNumber + 1;
  if (newState.throwNumber === 1) {
    return changeTurns(newState);
  }
  return newState;
};

export function endGame(cricketState: CricketGameState): CricketGameState {
  const newState = cloneDeep(cricketState);
  newState.gameState = GAME_STATE.FINISHED;
  newState.winner = getPlayersTeam(newState.currentPlayer);
  return newState;
}